// VirtualizedAutocomplete.jsx
import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FixedSizeList } from 'react-window';

const LISTBOX_PADDING = 8;

function renderRow(props) {
  const { data, index, style } = props;
  const option = data[index];
  return React.cloneElement(option, {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;

  const itemData = React.Children.toArray(children);

  const itemCount = itemData.length;
  const itemSize = 48;

  const outerRef = useRef();
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(outerRef.current);
      } else {
        ref.current = outerRef.current;
      }
    }
  }, [ref]);

  return (
    <div ref={outerRef} {...other}>
      <FixedSizeList
        height={Math.min(8, itemCount) * itemSize + 2 * LISTBOX_PADDING}
        itemCount={itemCount}
        itemSize={itemSize}
        width="100%"
        itemData={itemData}
        overscanCount={5}
      >
        {renderRow}
      </FixedSizeList>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};

const StyledOption = styled('li')(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.primary,
  backgroundColor: selected ? theme.palette.action.selected : theme.palette.background.paper,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const VirtualizedAutocomplete = React.memo(function VirtualizedAutocomplete(props) {
  const {
    options,
    value,
    onChange,
    label,
    optionKey,
    optionLabel,
    renderOption,
    filterOptions,
    disabled = false,
    ...other
  } = props;

  const memoizedOptions = useMemo(() => options, [options]);

  const renderOptionFn = renderOption || ((props, option, state) => (
    <StyledOption
      {...props}
      key={option[optionKey]}
      selected={state.selected}
    >
      {option[optionLabel] || ''}
    </StyledOption>
  ));

  const filterOptionsFn = filterOptions || ((options, state) => {
    const inputValue = state.inputValue.toLowerCase();
    return options.filter((option) =>
      (String(option[optionLabel]) || '').toLowerCase().includes(inputValue)
    );
  });

  const getOptionLabelFn = (option) => {
    if (option[optionLabel] === null || option[optionLabel] === undefined) return '';
    return String(option[optionLabel]) || ''
  };

  return (
    <Autocomplete
      {...other}
      disablePortal
      disableListWrap
      PopperComponent={Popper}
      ListboxComponent={ListboxComponent}
      options={memoizedOptions}
      value={value}
      onChange={onChange}

      isOptionEqualToValue={(option, value) => {
        return String(option[optionKey]) === String(value[optionKey]);
      }}

      className='w-full'


      sx={{
        justifySelf: 'stretch',
        '& .MuiInputBase-root': {
          height: '42px',
          fontSize: '16px',
        },
        '& .MuiInputLabel-root': {
          fontSize: '16px',
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"

          sx={{
            '& .MuiInputBase-root': {
              fontSize: '16px',
              height: '42px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '16px',
            },
          }}
        />
      )}
      renderOption={renderOptionFn}
      noOptionsText="No options found"
      getOptionLabel={getOptionLabelFn}
      filterOptions={filterOptionsFn}
      disabled={options.length === 0 || disabled}
    />
  );
});

VirtualizedAutocomplete.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  optionKey: PropTypes.string,
  optionLabel: PropTypes.string || PropTypes.number,
  renderOption: PropTypes.func,
  filterOptions: PropTypes.func,
  disabled: PropTypes.bool,
};

export default VirtualizedAutocomplete;
